import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Grid,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { addResource, updateResource } from '../../../services/ResourcesService';

const AddEditResourcesModal = ({ open, onClose, onSave, resource = null, locationId }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    location_id: parseInt(locationId),
    name: '',
    description: '',
    quantity: '',
    min_capacity: '',
    max_capacity: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (resource) {
      setFormData({
        location_id: parseInt(locationId),
        name: resource.name || '',
        description: resource.description || '',
        quantity: resource.quantity || '',
        min_capacity: resource.minCapacity || '',
        max_capacity: resource.maxCapacity || '',
      });
    } else {
      setFormData({
        location_id: parseInt(locationId),
        name: '',
        description: '',
        quantity: '',
        min_capacity: '',
        max_capacity: '',
      });
    }
  }, [resource, locationId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [field]: '' })); // Limpia errores en tiempo real
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('name_required');
    }
    if (!formData.description.trim()) {
      newErrors.description = t('description_required');
    }
    if (!formData.quantity || isNaN(formData.quantity) || parseInt(formData.quantity, 10) <= 0) {
      newErrors.quantity = t('quantity_required');
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return; // Si no pasa la validación, no continúa
    }

    setLoading(true);
    try {
      const payload = {
        location_id: formData.location_id,
        name: formData.name,
        description: formData.description,
        quantity: parseInt(formData.quantity, 10),
      };

      // Añade `min_capacity` y `max_capacity` solo si tienen valor
      if (formData.min_capacity) {
        payload.min_capacity = parseInt(formData.min_capacity, 10);
      }
      if (formData.max_capacity) {
        payload.max_capacity = parseInt(formData.max_capacity, 10);
      }

      let response;
      if (resource) {
        response = await updateResource(resource.id, payload);
      } else {
        response = await addResource(payload);
      }

      onSave(response); // Devuelve el recurso creado o editado al componente padre
      onClose(); // Cierra el modal
    } catch (error) {
      console.error('Error saving resource:', error);
      alert(t('error_saving_resource'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <span className="roboto-regular">
          {resource ? t('edit_resource') : t('add_resources')}
        </span>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: '4px' }}>
          {/* Nombre */}
          <Grid item xs={12}>
            <TextField
              label={t('name')}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          {/* Descripción */}
          <Grid item xs={12}>
            <TextField
              label={t('description')}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid>
          {/* Cantidad */}
          <Grid item xs={12}>
            <TextField
              label={t('quantity')}
              type="number"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', e.target.value)}
              fullWidth
              error={!!errors.quantity}
              helperText={errors.quantity}
            />
          </Grid>
          {/* Capacidad mínima */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('min_capacity')}
              type="number"
              value={formData.min_capacity}
              onChange={(e) => handleChange('min_capacity', e.target.value)}
              fullWidth
            />
          </Grid>
          {/* Capacidad máxima */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('max_capacity')}
              type="number"
              value={formData.max_capacity}
              onChange={(e) => handleChange('max_capacity', e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton loading={loading} onClick={handleSave} variant="contained">
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditResourcesModal;
